/**
 * Aaim Services Page (Sprint C17)
 *
 * Read-only inventory of internal Aaim platform services. This page is the
 * companion to the Integrations page (which lists third-party integrations only).
 * Internal services are NOT third-party integrations and have no enable/disable
 * toggle -- they are core platform infrastructure.
 *
 * Backend: GET /api/admin/integrations/aaim-services (returns isAaimProduct=true).
 */

import {
  CheckCircle2 as ConnectedIcon,
  AlertCircle as ErrorIcon,
  Settings as ConfigIcon,
  RefreshCw as RefreshIcon,
  AlertTriangle as WarningIcon,
} from 'lucide-react';
import {
  BodyText,
  Button,
  Card,
  MetricCard,
  TableScroll,
} from '@aaimplatform/ui';
import { useAaimServices, useTestIntegration } from '../hooks';
import type { Integration } from '../api/client';
import { useState } from 'react';

function getStatusIcon(status: string | undefined) {
  if (status === 'CONNECTED') return <ConnectedIcon className="text-growth" size={20} />;
  if (status === 'ERROR') return <ErrorIcon className="text-error" size={20} />;
  return <ConfigIcon className="text-tertiary" size={20} />;
}

function getStatusColor(status: string | undefined): string {
  switch (status) {
    case 'CONNECTED':
      return 'bg-growth/10 text-growth';
    case 'CONFIGURED':
      return 'bg-warning/10 text-warning';
    case 'ERROR':
      return 'bg-error/10 text-error';
    default:
      return 'bg-recessed text-secondary';
  }
}

export function AaimServicesPage() {
  const { data: services, isLoading, error, refetch } = useAaimServices();
  const testIntegration = useTestIntegration();
  const [testing, setTesting] = useState<string | null>(null);

  const handleTest = async (id: string) => {
    setTesting(id);
    try {
      await testIntegration.mutateAsync(id);
      refetch();
    } catch {
      // Error is reflected in the row status via refetch.
    } finally {
      setTesting(null);
    }
  };

  const list: Integration[] = services ?? [];
  const stats = {
    total: list.length,
    connected: list.filter((s) => s.status === 'CONNECTED').length,
    error: list.filter((s) => s.status === 'ERROR').length,
    other: list.filter((s) => s.status !== 'CONNECTED' && s.status !== 'ERROR').length,
  };

  if (error) {
    return (
      <div className="p-6">
        <Card className="p-6 text-center">
          <WarningIcon className="text-error mb-2" size={32} />
          <BodyText variant="ui" className="text-error mb-4">Failed to load Aaim Services</BodyText>
          <BodyText variant="ui" className="text-tertiary text-sm mb-4">{error.message}</BodyText>
          <Button variant="primary" onClick={() => refetch()}>
            <RefreshIcon className="mr-2" size={20} />
            Retry
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div>
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between mb-6">
        <div>
          <BodyText variant="ui" className="text-tertiary">
            Internal Aaim platform services. Read-only inventory -- core infrastructure cannot be disabled here.
          </BodyText>
        </div>
        <Button variant="secondary" onClick={() => refetch()} disabled={isLoading}>
          <RefreshIcon className={`mr-2 ${isLoading ? 'animate-spin' : ''}`} size={20} />
          Refresh All Status
        </Button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <MetricCard label="Total Services" value={String(stats.total)} />
        <MetricCard label="Connected" value={String(stats.connected)} />
        <MetricCard label="Error" value={String(stats.error)} />
        <MetricCard label="Other" value={String(stats.other)} />
      </div>

      <Card>
        <TableScroll label="Aaim services">
        <table className="w-full min-w-table-dense">
          <thead className="border-b border-recessed">
            <tr>
              <th className="text-left py-3 px-4 text-xs uppercase tracking-wide text-tertiary font-medium">
                Service
              </th>
              <th className="text-left py-3 px-4 text-xs uppercase tracking-wide text-tertiary font-medium">
                Subcategory
              </th>
              <th className="text-left py-3 px-4 text-xs uppercase tracking-wide text-tertiary font-medium">
                Status
              </th>
              <th className="text-left py-3 px-4 text-xs uppercase tracking-wide text-tertiary font-medium">
                Version
              </th>
              <th className="text-right py-3 px-4 text-xs uppercase tracking-wide text-tertiary font-medium">
                Actions
              </th>
            </tr>
          </thead>
          <tbody>
            {list.map((service) => {
              const status = service.status ?? 'NOT_CONFIGURED';
              return (
                <tr key={service.id} className="hover:bg-recessed border-b border-recessed">
                  <td className="py-3 px-4">
                    <div className="flex items-center gap-2">
                      {getStatusIcon(status)}
                      <div>
                        <BodyText variant="ui" className="font-medium text-sm">
                          {service.name}
                        </BodyText>
                        <BodyText variant="ui" className="text-tertiary text-xs">
                          {service.description ?? ''}
                        </BodyText>
                      </div>
                    </div>
                  </td>
                  <td className="py-3 px-4">
                    {service.subcategory && (
                      <span className="inline-block px-2 py-1 rounded text-xs bg-elevated text-secondary">
                        {service.subcategory}
                      </span>
                    )}
                  </td>
                  <td className="py-3 px-4">
                    <span
                      className={`inline-block px-2 py-1 rounded text-xs font-medium ${getStatusColor(status)}`}
                    >
                      {status.replace('_', ' ')}
                    </span>
                  </td>
                  <td className="py-3 px-4">
                    <BodyText variant="ui" className="text-sm text-secondary">
                      {service.version ?? '-'}
                    </BodyText>
                  </td>
                  <td className="py-3 px-4 text-right">
                    <button
                      onClick={() => handleTest(service.id)}
                      disabled={testing === service.id}
                      className="px-3 py-1 text-sm hover:bg-recessed rounded transition-colors disabled:opacity-50"
                      title="Test health endpoint"
                    >
                      {testing === service.id ? 'Testing...' : 'Test'}
                    </button>
                  </td>
                </tr>
              );
            })}
            {!isLoading && list.length === 0 && (
              <tr>
                <td colSpan={5} className="py-8 text-center">
                  <BodyText variant="ui" className="text-tertiary">
                    No Aaim services found.
                  </BodyText>
                </td>
              </tr>
            )}
          </tbody>
        </table>
        </TableScroll>
      </Card>
    </div>
  );
}
